export type FullscreenResult = "entered" | "exited" | "unsupported" | "failed";

interface WebkitFullscreenDocument extends Document {
  readonly webkitFullscreenElement?: Element | null;
  readonly webkitFullscreenEnabled?: boolean;
  webkitExitFullscreen?: () => Promise<void> | void;
}

interface WebkitFullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

export function isFullscreenActive(documentRef: Document = document): boolean {
  const webkitDocument = documentRef as WebkitFullscreenDocument;
  return Boolean(documentRef.fullscreenElement ?? webkitDocument.webkitFullscreenElement);
}

export function isFullscreenSupported(documentRef: Document = document): boolean {
  const webkitDocument = documentRef as WebkitFullscreenDocument;
  const root = documentRef.documentElement as WebkitFullscreenElement;
  return Boolean(
    documentRef.fullscreenEnabled ||
      webkitDocument.webkitFullscreenEnabled ||
      root.requestFullscreen ||
      root.webkitRequestFullscreen,
  );
}

export async function requestDocumentFullscreen(
  documentRef: Document = document,
): Promise<FullscreenResult> {
  if (isFullscreenActive(documentRef)) return "entered";
  if (!isFullscreenSupported(documentRef)) return "unsupported";

  const root = documentRef.documentElement as WebkitFullscreenElement;
  const request = root.requestFullscreen?.bind(root) ?? root.webkitRequestFullscreen?.bind(root);
  if (!request) return "unsupported";
  try {
    await request();
    return "entered";
  } catch {
    return "failed";
  }
}

export async function toggleDocumentFullscreen(
  documentRef: Document = document,
): Promise<FullscreenResult> {
  if (!isFullscreenActive(documentRef)) return requestDocumentFullscreen(documentRef);

  const webkitDocument = documentRef as WebkitFullscreenDocument;
  const exit =
    documentRef.exitFullscreen?.bind(documentRef) ??
    webkitDocument.webkitExitFullscreen?.bind(webkitDocument);
  if (!exit) return "unsupported";
  try {
    await exit();
    return "exited";
  } catch {
    return "failed";
  }
}

export function shouldOfferMobileFullscreen(
  isMobile: boolean,
  fullscreenActive: boolean,
  dismissed: boolean,
): boolean {
  return isMobile && !fullscreenActive && !dismissed;
}
